import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { firstValueFrom } from 'rxjs';
import { ModalComponent } from '../../components/modal/modal.component';
import { ButtonDirective } from '../../components/button/button.directive';
import { SelectComponent } from '../../components/select/select.component';
import { FilterOption } from '../../components/table/table.interface';
import { DictionaryService } from '../../services/dictionary.service';
import { SnackbarService } from '../../services/snackbar.service';
import { AudioRecorderComponent } from './ui/audio-recorder/ticket-audio-recorder.component';
import {
  TicketCategory,
  TicketPriority,
} from './domain/ticket.models';
import {
  mergeTicketFileSelection,
  TICKET_MESSAGE_FILE_ACCEPT,
  TicketFileSelectionError,
} from './domain/ticket-file-selection';
import { TicketImagePreviewCache } from './domain/ticket-image-preview-cache';
import { ticketAttachmentKind } from './domain/ticket-attachment-types';
import { TicketApiService } from './data-access/ticket-api.service';
import { TicketAttachmentPreviewComponent } from './ui/attachment-preview/ticket-attachment-preview.component';
import {
  TicketSelectedAttachmentEvent,
  TicketSelectedUploadsComponent,
} from './ui/selected-uploads/ticket-selected-uploads.component';
import { ticketApiErrorMessage } from './domain/ticket-errors';

interface SelectedAttachmentPreview {
  file: File;
  url?: string;
}

@Component({
  selector: 'app-create-ticket-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModalComponent,
    SelectComponent,
    AudioRecorderComponent,
    ButtonDirective,
    NgIcon,
    TicketAttachmentPreviewComponent,
    TicketSelectedUploadsComponent,
  ],
  templateUrl: './create-ticket-modal.component.html',
  styleUrl: './create-ticket-modal.component.scss',
})
export class CreateTicketModalComponent implements OnChanges, OnDestroy {
  readonly isOpen = input(false);
  readonly closed = output<void>();
  readonly created = output<string>();
  @ViewChild(TicketAttachmentPreviewComponent)
  private attachmentViewer?: TicketAttachmentPreviewComponent;

  readonly dictionary = inject(DictionaryService);
  readonly modalOpened = signal(false);
  readonly submitting = signal(false);
  readonly attachments = signal<File[]>([]);
  readonly audio = signal<File | null>(null);
  readonly audioRecorded = signal(false);
  readonly video = signal<File | null>(null);
  readonly selectedImagePreview = signal<SelectedAttachmentPreview | null>(null);
  readonly selectedUploadCount = computed(
    () => this.attachments().length + Number(!!this.audio()) + Number(!!this.video()),
  );
  readonly categories = Object.values(TicketCategory);
  readonly priorities = Object.values(TicketPriority);
  readonly categoryOptions = this.categories.map((category) =>
    this.dictionary.get(category),
  );
  readonly priorityOptions = this.priorities.map((priority) =>
    this.dictionary.get(priority),
  );
  readonly messageFileAccept = TICKET_MESSAGE_FILE_ACCEPT;

  private readonly formBuilder = inject(FormBuilder);
  readonly form = this.formBuilder.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(200)]],
    description: [''],
    category: new FormControl<TicketCategory | null>(null, Validators.required),
    priority: new FormControl<TicketPriority | null>(null, Validators.required),
  });

  private readonly service = inject(TicketApiService);
  private readonly snackbar = inject(SnackbarService);
  private readonly imagePreviewCache = new TicketImagePreviewCache();
  readonly imagePreview = (file: File): string | null =>
    this.imagePreviewCache.urlFor(file);
  private selectedImagePreviewTrigger?: HTMLElement;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      this.modalOpened.set(this.isOpen());
      if (this.isOpen()) this.reset();
      else this.clearSelectedImageState();
    }
  }

  ngOnDestroy(): void {
    this.closeSelectedImagePreview(false);
    this.imagePreviewCache.clear();
  }

  selectFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const currentAudio = this.audio();
    const result = mergeTicketFileSelection({
      attachments: this.attachments(),
      audio: currentAudio,
      video: this.video(),
    }, Array.from(input.files ?? []));
    input.value = '';

    if (result.error) {
      this.snackbar.showError(this.fileSelectionError(result.error));
      return;
    }
    if (!result.files) return;
    this.attachments.set(result.files.attachments);
    this.imagePreviewCache.sync(result.files.attachments);
    this.audio.set(result.files.audio);
    this.video.set(result.files.video);
    if (result.files.audio !== currentAudio) {
      this.audioRecorded.set(false);
    }
  }
  setRecordedAudio(file: File | null): void {
    this.audio.set(file);
    this.audioRecorded.set(!!file);
  }
  clearAudio(): void {
    this.audio.set(null);
    this.audioRecorded.set(false);
  }
  clearVideo(): void {
    this.video.set(null);
  }
  removeAttachment(index: number): void {
    const removedFile = this.attachments()[index];
    const nextAttachments = this.attachments().filter((_, current) => current !== index);
    if (this.selectedImagePreview()?.file === removedFile) {
      this.closeSelectedImagePreview(false);
    }
    this.attachments.set(nextAttachments);
    this.imagePreviewCache.sync(nextAttachments);
  }
  imagePreviewUrl(file: File): string | null {
    return this.imagePreviewCache.urlFor(file);
  }
  previewSelectedImage(file: File, event: Event): void {
    const url = this.imagePreviewCache.urlFor(file);
    if (!url) return;
    this.selectedImagePreviewTrigger = event.currentTarget as HTMLElement;
    this.selectedImagePreview.set({ file, url });
    this.attachmentViewer?.openImage(
      file.name,
      url,
      this.selectedImagePreviewTrigger,
    );
  }
  async openSelectedAttachment(event: TicketSelectedAttachmentEvent): Promise<void> {
    this.selectedImagePreviewTrigger = event.trigger;
    if (ticketAttachmentKind(event.file.name, event.file.type) === 'spreadsheet') {
      this.selectedImagePreview.set({ file: event.file });
      await this.attachmentViewer?.openSpreadsheetFile(event.file, event.trigger);
      return;
    }
    const url = this.imagePreviewCache.urlFor(event.file);
    if (!url) return;
    this.selectedImagePreview.set({ file: event.file, url });
    this.attachmentViewer?.openImage(event.file.name, url, event.trigger);
  }
  selectedImageLoadFailed(file: File): void {
    if (this.selectedImagePreview()?.file === file) {
      this.closeSelectedImagePreview(false);
    }
    this.imagePreviewCache.markFailed(file);
  }
  closeSelectedImagePreview(restoreFocus = true): void {
    this.selectedImagePreviewTrigger = undefined;
    this.attachmentViewer?.close(restoreFocus);
    this.selectedImagePreview.set(null);
  }
  selectedImagePreviewClosed(): void {
    this.selectedImagePreview.set(null);
    this.selectedImagePreviewTrigger = undefined;
  }
  formatFileSize(file: File): string {
    if (file.size < 1024 * 1024) return `${Math.max(1, Math.round(file.size / 1024))} KB`;
    return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
  }
  canSubmit(): boolean {
    return this.form.valid
      && !!this.form.controls.title.value?.trim()
      && (
        !!this.form.controls.description.value?.trim()
        || this.attachments().length > 0
        || !!this.audio()
        || !!this.video()
      )
      && !this.submitting();
  }
  attachmentPreviewFailed(): void {
    this.selectedImagePreview.set(null);
    this.selectedImagePreviewTrigger = undefined;
    this.snackbar.showError(this.dictionary.get('AttachmentPreviewFailed'));
  }

  selectCategory(option: FilterOption): void {
    this.form.controls.category.setValue(option.id as TicketCategory);
  }

  selectPriority(option: FilterOption): void {
    this.form.controls.priority.setValue(option.id as TicketPriority);
  }

  async submit(): Promise<void> {
    if (!this.canSubmit()) { this.form.markAllAsTouched(); return; }
    const value = this.form.getRawValue();
    if (!value.category || !value.priority) {
      this.form.controls.category.markAsTouched();
      this.form.controls.priority.markAsTouched();
      return;
    }
    this.submitting.set(true);
    try {
      const description = value.description.trim() || undefined;
      const response = await firstValueFrom(this.service.create({
        title: value.title.trim(),
        description,
        category: value.category,
        priority: value.priority,
        attachments: this.attachments(),
        audio: this.audio(),
        video: this.video(),
      }));
      this.snackbar.showSuccess('TicketCreated');
      this.created.emit(response.ticket.ticketNumber);
      this.handleClose();
    } catch (error: unknown) {
      this.snackbar.showError(ticketApiErrorMessage(error, 'TicketCreateFailed'));
    } finally { this.submitting.set(false); }
  }
  handleClose(): void { this.modalOpened.set(false); this.reset(); this.closed.emit(); }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (!this.modalOpened()) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.selectedImagePreview()) {
      this.closeSelectedImagePreview();
      return;
    }
    this.handleClose();
  }

  private reset(): void {
    this.clearSelectedImageState();
    this.form.reset({
      title: '',
      description: '',
      category: null,
      priority: null,
    });
    this.attachments.set([]);
    this.clearAudio();
    this.clearVideo();
  }
  private clearSelectedImageState(): void {
    this.closeSelectedImagePreview(false);
    this.imagePreviewCache.clear();
  }
  private fileSelectionError(error: TicketFileSelectionError): string {
    const keys: Record<TicketFileSelectionError, string> = {
      unsupported: 'TicketUnsupportedFile',
      size: 'TicketFileTooLarge',
      attachments: 'TicketAttachmentLimit',
      audio: 'TicketAudioLimit',
      video: 'TicketVideoLimit',
    };
    return keys[error];
  }
}

